import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import * as PropTypes from 'prop-types';
import { format } from 'date-fns';
import apps from '../../constants/Apps';
import { isLimitedAccessRole, UserHasPermission } from '../../functions/AuthorizationFunctions';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import ProductsSection from '../Products/ProductsSection';
import LandingPageContent from './content/LandingPageContent';
import { PermissionCategories } from '../../constants/Enums';
import {
    AccountingApiRoutes,
    requestTypes,
    ReconciliationApiRoutes
} from '../../constants/CommonApiRoutes';
import { getWcUrl } from '../../api/WolfConnectApi';
import config from '../../config';
import { Alert, AlertSizes, AlertTypes } from '../common/Alert';
import { setStateData } from '../common/StateHelper';
import { getToken } from '../../functions/AuthenticationFunctions';
import LicenseModal from './licenseAgreement/LicenseModal';

const AppLandingPage = props => {
    const {
        user,
        error,
        isLoading,
        permissions,
        isSalesforceUser,
        history,
        setFederationId,
        httpRequest,
        showBRIcon,
        showBOIcon
    } = props;
    const [landingPageState, setLandingPageState] = useState({
        wcUrl: null,
        wcFetched: false,
        isAlertActive: false,
        alertMessage: '',
        showBRIcon: !!showBRIcon,
        brLicenseAccepted: false,
        modalIsOpen: false,
        memberId: '',
        landingUrl: '',
        showBOIcon: false
    });

    const dispatch = useDispatch();
    const features = useSelector(state => state.securityGroup.features);
    const toggleModalStatus = () => { setStateData('modalIsOpen', !landingPageState.modalIsOpen, setLandingPageState) };
    const acceptLicenceAgreement = (isChecked) => {
        isChecked && httpRequest(
            ReconciliationApiRoutes.GET_RECONCILIATION_MEMBER_SETTINGS_URL + '/' + landingPageState.memberId,
            requestTypes.PATCH,
            { 'licenceAgreementAccepted': true },
            () => {
                setStateData('brLicenseAccepted', true, setLandingPageState)
            },
            errorMessage => {
                setLandingPageState(prevState => ({
                    ...prevState,
                    isAlertActive: true,
                    alertMessage: errorMessage
                }));
            }
        );
        toggleModalStatus();
        window.open(`${landingPageState.landingUrl}signin?access_token=${getToken()}`);
    }

    const getProductAction = app => {
        const lwt = user.lwt;
        switch (app) {
            case apps.PERMISSIONS:
                history.push(app.landingUrl);
                break;
            case apps.COMMUNITY_PORTAL:
                setStateData('isLoading', true, setLandingPageState);

                if (isSalesforceUser) {
                    dispatch(setFederationId(lwt.uid, lwt.cid, success => {
                        window.location = config.get(success ? 'SALESFORCE_URL' : 'PORTAL_LANDING_URL');
                    }));
                } else {
                    window.location = app.landingUrl;
                }
                break;
            case apps.WOLF_CONNECT:
                window.open(landingPageState.wcUrl);
                break;
            case apps.QUICK_BOOKS:
                httpRequest(
                    AccountingApiRoutes.GET_QBO_CONFIG_URL,
                    requestTypes.GET,
                    null,
                    response => {
                        window.open(response.data ? response.data.url : app.landingUrl);
                    },
                    errorMessage => {
                        setLandingPageState(prevState => ({
                            ...prevState,
                            isAlertActive: true,
                            alertMessage: errorMessage
                        }));
                    }
                );
                break;
            case apps.INSIGHTS:
                window.open(`${app.landingUrl}signin?access_token=${getToken()}`);
                break;
            case apps.BANKING_RECONCILIATION:
                if (!landingPageState.brLicenseAccepted) {
                    setLandingPageState(prevState => ({
                        ...prevState,
                        modalIsOpen: true,
                        landingUrl: app.landingUrl
                    }));
                } else {
                    window.open(`${app.landingUrl}signin?access_token=${getToken()}`);
                }
                break;
            case apps.BACK_OFFICE_V4:
                window.open(`${app.landingUrl}signin?access_token=${getToken()}`);
                break;
            default:
        }
    };

    const setAlertActive = isActive => setStateData('isAlertActive', isActive, setLandingPageState);

    const getShowBRIconSetting = (featureIds) => {
        const callbackAlertMessage = errorMessage => {
            setLandingPageState(prevState => ({
                ...prevState,
                isAlertActive: true,
                alertMessage: errorMessage
            }));
        };
        if (featureIds?.includes('BR')) {
            httpRequest(
                ReconciliationApiRoutes.GET_CLIENT_SETTINGS_URL,
                requestTypes.GET,
                null,
                response => setStateData('showBRIcon', response.data.modernViewEnabled, setLandingPageState),
                callbackAlertMessage
            );
            httpRequest(
                ReconciliationApiRoutes.GET_RECONCILIATION_MEMBER_SETTINGS_URL,
                requestTypes.GET,
                null,
                response => {
                    setLandingPageState(prevState => ({
                        ...prevState,
                        brLicenseAccepted : response.data.licenceAgreementAccepted,
                        memberId: response.data.id
                    }));
                },
                callbackAlertMessage
            );
        }
    };

    const getShowBOIconSetting = (featureIds) => {
        if (featureIds?.includes(apps.BACK_OFFICE_V4.appId)) {
            setStateData('showBOIcon', true, setLandingPageState);
        }
    };

    useEffect(() => {
        !landingPageState.wcFetched && dispatch(getWcUrl(user.lwt, url => {
            setLandingPageState(prevState => ({
                ...prevState,
                wcUrl: url,
                wcFetched: true
            }));
        }));
    }, [landingPageState.wcFetched]);

    useEffect(() => {
        if (!landingPageState.showBRIcon) {
            const currentDate = format(new Date(Date.now()), 'yyyy-MM-dd');
            const filterFeatures = features?.filter(item => {
                if (!!item.startDate && new Date(item.startDate) > new Date(currentDate)) {
                    return false;
                }
                if (!!item.endDate && new Date(item.endDate) < new Date(currentDate)) {
                    return false;
                }
                return true;
            });
            const featureIds = filterFeatures.map(item => item.id);
            getShowBRIconSetting(featureIds);
        }
    }, [features]);

    useEffect(() => {
        if (!landingPageState.showBOIcon) {
            const currentDate = format(new Date(Date.now()), 'yyyy-MM-dd');
            const filterFeatures = features?.filter(item => {
                if (!!item.startDate && new Date(item.startDate) > new Date(currentDate)) {
                    return false;
                }
                if (!!item.endDate && new Date(item.endDate) < new Date(currentDate)) {
                    return false;
                }
                return true;
            });

            const featureIds = filterFeatures.map(item => item.id);
            getShowBOIconSetting(featureIds)
        }
    }, [features]);

    const getAppCards = () => {
        let _apps = [];

        for (let app in apps) {
            let appId = apps[app].appId;
            if (user?.products?.some(p => p === appId)) {
                if (appId !== 'BR') {
                    _apps.push(apps[app]);
                }
            }
        }

        if (UserHasPermission(permissions, PermissionCategories.BANKING_RECONCILIATION)) {
            if (landingPageState.showBRIcon) {
                _apps.push(apps.BANKING_RECONCILIATION);
            }
        }

        if (UserHasPermission(permissions, PermissionCategories.ROLES)) {
            _apps.push(apps.PERMISSIONS);
        }

        _apps.push(apps.COMMUNITY_PORTAL);
        if (landingPageState.wcUrl) {
            _apps.push(apps.WOLF_CONNECT);
        }

        if (UserHasPermission(permissions, PermissionCategories.QUICK_BOOKS)) {
            _apps.push(apps.QUICK_BOOKS)
        }

        if (landingPageState.showBOIcon) {
            _apps.push(apps.BACK_OFFICE_V4);
        }

        return _apps;
    };

    const isLimitedAccess = isLimitedAccessRole(permissions);
    const licenceModal = landingPageState.modalIsOpen
        ? (
            <LicenseModal
                show={landingPageState.modalIsOpen}
                closeModal={() => toggleModalStatus()}
                acceptLicence={(isChecked) => acceptLicenceAgreement(isChecked)}
            />
        )
        : null;
    if (error) {
        const errorMsg = error?.code === 500 ? 'An Unknown Error Occurred' : 'You Do Not Have Access';
        return (
            <div className="container content row mt-4">
                <div className="col-sm-12 text-center">
                    <h1>{errorMsg}</h1>
                </div>
            </div>
        );
    } else {

        return (<>
                <LoadingOverlay loading={isLoading}>
                    <ProductsSection
                        apps={getAppCards()}
                        action={getProductAction}
                        isLimitedAccess={isLimitedAccess}
                    />
                    <LandingPageContent />
                </LoadingOverlay>
                <Alert
                    onRequestClose={() => setAlertActive(false)}
                    content={landingPageState.alertMessage}
                    isOpen={landingPageState.isAlertActive}
                    size={AlertSizes.LARGE}
                    type={AlertTypes.WARNING}
                    iconType={'warning'}
                    heading={'WARN'}
                />
                {licenceModal}
            </>
        );
    }
};

AppLandingPage.propTypes = {
    user: PropTypes.object,
    error: PropTypes.object,
    isLoading: PropTypes.bool.isRequired,
    permissions: PropTypes.object.isRequired,
    isSalesforceUser: PropTypes.bool.isRequired,
    history: PropTypes.object,
    setFederationId: PropTypes.func,
    httpRequest: PropTypes.func,
    getWcUrl: PropTypes.func,
    showBRIcon: PropTypes.bool,
    showBOIcon: PropTypes.bool
};

export default withRouter(AppLandingPage);